import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { PaymentRunCreateModal } from './PaymentRunCreateModal.jsx';
import { PaymentRunDetailModal } from './PaymentRunDetailModal.jsx';

const STATUS_VARIANT = { PROPOSED: 'warning', EXECUTED: 'success', CANCELLED: 'neutral' };

export function PaymentRunsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['finance', 'payment-runs'], queryFn: () => financeApi.listPaymentRuns().then((r) => r.items) });
  const rows = list.data || [];

  const columns = [
    { key: 'runNumber', label: t('pr.col.number'), render: (r) => <span className="mono">{r.runNumber}</span> },
    { key: 'paymentDate', label: t('pr.col.paymentDate'), render: (r) => formatDate(r.paymentDate) },
    { key: 'dueBy', label: t('pr.col.dueBy'), render: (r) => formatDate(r.dueBy) },
    { key: 'count', label: t('pr.col.count'), align: 'right', render: (r) => <span className="mono num">{r.itemCount}</span> },
    { key: 'total', label: t('pr.col.total'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.totalAmount)}</span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`pr.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('pr.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('pr.title')}</h1>
          <p className="muted">{t('pr.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('pr.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('pr.metric.proposed')} value={String(rows.filter((r) => r.status === 'PROPOSED').length)} trendVariant="warning" />
        <MetricCard label={t('pr.metric.executed')} value={String(rows.filter((r) => r.status === 'EXECUTED').length)} trendVariant="success" />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('pr.empty')} />

      <PaymentRunCreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => setDetailId(id)} />
      <PaymentRunDetailModal runId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
